const FIRST_CASH_PAYMENT_TYPE =
  '#payment-options-group .vd-btn[data-id="1"]:first-child'

/**
 * Constants related to training modes & tours.
 */

export const ONBOARDING_PRODUCT_SKU = '10012'

export const USER_ATTRIBUTES = {
  // This attribute is false when user "has gone through on-boarding" (i.e. either migration-from-old-sell-screen
  // on-boarding or first time use on-boarding). Note that this is only relevant to the onboarding-tour-on-load scenario.
  // If the user enabled training mode and toggles tour on, the tour will show regardless of this attribute.
  USER_IS_NEW_TO_REGISTER: 'initiate_web_register_sale_training',

  // Should be one primary user per account. This is the user assumed to be the bill payer & decision maker, so we
  // update copy slightly during tours for this user.
  USER_IS_PRIMARY: 'is_primary_user',
}

export const RETAILER_SETTINGS = {
  // This attribute is false when user has both old sell screen and web register
  // This is used to know which on-boarding tour type to display (i.e. migration-from-old-sell-screen or branch new
  // to Vend first time use on-boarding)
  REGISTER_IS_DEFAULT: 'has_web_register',
} as const

export enum TOUR_TYPE {
  // This type is used for migration-from-old-sell-screen on-boarding tour, as well as general training mode tour
  TRAINING = 'training',

  // This type is used for primary user on-boarding tour.
  PRIMARY_ONBOARDING = 'primary-onboarding',

  // This type is used for all users on-boarding tour except for primary user.
  ONBOARDING = 'onboarding',
}

export interface TourStep {
  id: string
  title: string
  text: string
  pagination?: boolean
  nextBtn?: boolean
  attachTo: string | unknown
  triggerOn?: string | string[]
  trackingData?: { [key: string]: { step: string } }
  tetherOptions?: unknown
  delay?: number
}

interface TourSteps {
  [tour: string]: TourStep[]
}

// Defines the step configuration for different tour types. Uses Shepherd & Tether
// http://github.hubspot.com/shepherd/ && http://github.hubspot.com/tether/
export const TOUR_STEPS: TourSteps = {
  TRAINING: [
    {
      id: 'addProduct',
      title: 'Add a product to your sale',
      text:
        'Add a product to your sale, simply search or scan a barcode to add an item to your sale.',

      attachTo: '.wr-global-search right',
      triggerOn: 'training-sale:complete',
    },
    {
      id: 'addDiscount',
      title: 'Add a discount',
      text:
        'Click the product to discount it. You can also change the quantity and add a note.',

      attachTo: '.li-cell left',
      triggerOn: 'sale:add-line-item',
    },
    {
      id: 'addCustomer',
      title: 'Record who’s buying',
      text: 'Search for a customer to link the sale to their name.',

      attachTo: '.wr-customer-search right',
      triggerOn: 'sale:line-item-price-adjusted:no-customer',
    },
    {
      id: 'takePayment',
      title: 'Take payment',
      text: 'Click the "Pay" button to finalise the sale.',

      attachTo: '.sale-action-bar top',
      triggerOn: [
        'search:customer-selected',
        'autocomplete:customer-selected',
        'training-sale:dismiss-payment-screen',
      ],
    },
    {
      id: 'choosePaymentType',
      title: 'Choose a payment type',
      text: `Pick the payment type the customer wishes to use. You can split payments by typing a smaller amount
        into the tender field.`,

      attachTo: '#payment-options-group bottom',
      triggerOn: 'sale:payment',
      delay: 400,
    },
    {
      id: 'exit',
      title: 'Got the hang of it?',
      text:
        'Once you’ve got the hang of this, click here to exit Training Mode.',
      nextBtn: true,

      attachTo: '.register-info-popover-trigger right',
      triggerOn: [
        'sale:transitioning-to-status',
        'training-sale:force-sale-confirmation-screen',
      ],
      tetherOptions: {
        targetAttachment: 'top left',
        offset: '-20px -120px',
      },
    },
  ],

  PRIMARY_ONBOARDING: [
    {
      id: 'searchBar',
      title: 'Search for a product',
      text:
        'The search is designed to be fast and forgiving of spelling mistakes. You can also scan barcodes.',
      pagination: true,

      attachTo: '.wr-global-search right',
      tetherOptions: {
        attachment: 'top left',
        targetAttachment: 'top right',
        offset: '0px -10px',
      },
    },
    {
      id: 'payment',
      title: 'Click “Pay” to continue the sale',
      text:
        "After adding all items to the checkout, you're ready to make a payment.",
      pagination: true,
      attachTo: '.sale-action-bar top',
      triggerOn: ['training-sale:dismiss-payment-screen', 'sale:add-line-item'],
      trackingData: {
        'sale:add-line-item': {
          step: 'Product Added to Sale',
        },
      },
    },
    {
      id: 'paymentOptions',
      title: 'Select Cash',
      text:
        "Your Lightspeed Retail store can have many payment types. We've set up Cash for you to try.",
      pagination: true,
      attachTo: {
        element: FIRST_CASH_PAYMENT_TYPE,
        on: 'bottom',
      },
      triggerOn: 'sale:payment',
      delay: 600,
      trackingData: {
        'sale:payment': {
          step: 'Clicked Pay Button',
        },
      },
    },
    {
      id: 'exit',
      title: 'Complete Tutorial',
      text:
        'Click here to complete this tutorial and start setting up your store.',
      pagination: true,

      attachTo: '.sale-confirmation-form-row right',
      triggerOn: [
        'sale:transitioning-to-status',
        'training-sale:force-sale-confirmation-screen',
      ],
      delay: 400,
    },
  ],

  ONBOARDING: [
    {
      id: 'searchBar',
      title: 'Search for a product name or scan a barcode',
      text:
        'The search is designed to be fast and forgiving of spelling mistakes. Click the result to add it to the checkout list.',
      pagination: true,

      attachTo: '.wr-global-search right',
      tetherOptions: {
        attachment: 'top left',
        targetAttachment: 'top right',
        offset: '0px -10px',
      },
    },
    {
      id: 'payment',
      title: 'Click “Pay” to continue the sale',
      text:
        "After adding all items to the checkout, you're ready to make a payment.",
      pagination: true,
      attachTo: '.sale-action-bar top',
      triggerOn: ['training-sale:dismiss-payment-screen', 'sale:add-line-item'],
      trackingData: {
        'sale:add-line-item': {
          step: 'Product Added to Sale',
        },
      },
    },
    {
      id: 'paymentOptions',
      title: 'Select Cash',
      text:
        "Your Lightspeed Retail store can have many payment types. We've set up Cash for you to try.",
      pagination: true,
      attachTo: {
        element: FIRST_CASH_PAYMENT_TYPE,
        on: 'bottom',
      },
      triggerOn: 'sale:payment',
      delay: 600,
      trackingData: {
        'sale:payment': {
          step: 'Clicked Pay Button',
        },
      },
    },
    {
      id: 'exit',
      title: 'Complete Tutorial',
      text:
        'Click here to complete this tutorial and start setting up your store.',
      pagination: true,

      attachTo: '.sale-confirmation-form-row right',
      triggerOn: [
        'sale:transitioning-to-status',
        'training-sale:force-sale-confirmation-screen',
      ],
      delay: 400,
    },
  ],
}
